import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
  FaArrowLeft, 
  FaSpinner, 
  FaUser, 
  FaEnvelope, 
  FaPhone, 
  FaBuilding,
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaClock,
  FaTruck,
  FaBoxOpen,
  FaCheckCircle,
  FaTimesCircle
} from 'react-icons/fa';
import api from '../../services/api';
import './QuoteDetail.css';

function QuoteDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchQuote();
  }, [id]);

  const fetchQuote = async () => {
    try {
      const response = await api.get(`/api/admin/quotes/${id}`);
      setQuote(response.data);
    } catch (error) {
      console.error('Error fetching quote:', error);
      setError('Failed to load quote details');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="quote-detail-loading">
        <FaSpinner className="spinner" />
        <p>Loading quote details...</p>
      </div>
    );
  }

  if (error || !quote) {
    return (
      <div className="quote-detail-error">
        <p>{error || 'Quote not found'}</p>
        <Link to="/admin/dashboard/quotes" className="back-btn">
          <FaArrowLeft /> Back to Quotes
        </Link>
      </div>
    );
  }

  return (
    <div className="quote-detail-page">
      <div className="quote-detail-header">
        <Link to="/admin/dashboard/quotes" className="back-link">
          <FaArrowLeft /> Back to Quotes
        </Link>
        <h2>Quote Request Details</h2>
        <span className="quote-date">Received: {formatDate(quote.createdAt)}</span>
      </div>

      <div className="quote-detail-grid">
        {/* Contact Details */}
        <div className="detail-section">
          <h3>
            <FaUser /> Contact Information
          </h3>
          <div className="detail-content">
            <div className="detail-row">
              <label>Full Name:</label>
              <span>{quote.fullName}</span>
            </div>
            {quote.companyName && (
              <div className="detail-row">
                <label><FaBuilding /> Company:</label>
                <span>{quote.companyName}</span>
              </div>
            )}
            <div className="detail-row">
              <label><FaEnvelope /> Email:</label>
              <a href={`mailto:${quote.emailAddress}`}>{quote.emailAddress}</a>
            </div>
            <div className="detail-row">
              <label><FaPhone /> Phone:</label>
              <a href={`tel:${quote.phoneNumber}`}>{quote.phoneNumber}</a>
            </div>
          </div>
        </div>

        {/* Collection Details */}
        <div className="detail-section collection-section">
          <h3>
            <FaMapMarkerAlt /> Collection Details
          </h3>
          <div className="detail-content">
            <div className="detail-row">
              <label>Address:</label>
              <span>{quote.collectionAddress}</span>
            </div>
            <div className="detail-row">
              <label>Postcode:</label>
              <span className="postcode">{quote.collectionPostcode}</span>
            </div>
            <div className="detail-row">
              <label>Contact Person:</label>
              <span>{quote.collectionContactName}</span>
            </div>
            <div className="detail-row">
              <label><FaCalendarAlt /> Date:</label>
              <span>{quote.collectionDate}</span>
            </div>
            <div className="detail-row">
              <label><FaClock /> Time:</label>
              <span>{quote.collectionTime}</span>
            </div>
            <div className="detail-row">
              <label>Flexible Time:</label>
              <span className={`flex-badge ${quote.collectionFlexible === 'yes' ? 'yes' : 'no'}`}>
                {quote.collectionFlexible === 'yes' ? <><FaCheckCircle /> Yes</> : <><FaTimesCircle /> No</>}
              </span>
            </div>
          </div>
        </div>

        {/* Delivery Details */}
        <div className="detail-section delivery-section">
          <h3>
            <FaMapMarkerAlt /> Delivery Details
          </h3>
          <div className="detail-content">
            <div className="detail-row">
              <label>Address:</label>
              <span>{quote.deliveryAddress}</span>
            </div>
            <div className="detail-row">
              <label>Postcode:</label>
              <span className="postcode">{quote.deliveryPostcode}</span>
            </div>
            <div className="detail-row">
              <label>Contact Person:</label>
              <span>{quote.deliveryContactName}</span>
            </div>
            <div className="detail-row">
              <label><FaCalendarAlt /> Date:</label>
              <span>{quote.deliveryDate}</span>
            </div>
            <div className="detail-row">
              <label><FaClock /> Time:</label>
              <span>{quote.deliveryTime}</span>
            </div>
            <div className="detail-row">
              <label>Flexible Time:</label>
              <span className={`flex-badge ${quote.deliveryFlexible === 'yes' ? 'yes' : 'no'}`}>
                {quote.deliveryFlexible === 'yes' ? <><FaCheckCircle /> Yes</> : <><FaTimesCircle /> No</>}
              </span>
            </div>
          </div>
        </div>

        {/* Shipment Information */}
        <div className="detail-section shipment-section">
          <h3>
            <FaBoxOpen /> Shipment Information
          </h3>
          <div className="detail-content">
            <div className="detail-row">
              <label>Type of Goods:</label>
              <span>{quote.typeOfGoods}</span>
            </div>
            <div className="detail-row">
              <label>Quantity:</label>
              <span>{quote.quantity}</span>
            </div>
            <div className="detail-row">
              <label>Dimensions:</label>
              <span>{quote.dimensions}</span>
            </div>
            <div className="detail-row">
              <label>Weight:</label>
              <span>{quote.weight}</span>
            </div>
            <div className="detail-row">
              <label>Fragile:</label>
              <span className={`status-badge ${quote.isFragile === 'yes' ? 'warning' : 'success'}`}>
                {quote.isFragile === 'yes' ? '⚠️ Yes' : '✓ No'}
              </span>
            </div>
            <div className="detail-row">
              <label>Temperature Control:</label>
              <span className={`status-badge ${quote.requiresTempControl === 'yes' ? 'info' : 'success'}`}>
                {quote.requiresTempControl === 'yes' ? '🌡️ Yes' : '✓ No'}
              </span>
            </div>
            <div className="detail-row">
              <label>Two-Man Handling:</label>
              <span className={`status-badge ${quote.requiresTwoMan === 'yes' ? 'info' : 'success'}`}>
                {quote.requiresTwoMan === 'yes' ? '👥 Yes' : '✓ No'}
              </span>
            </div>
          </div>
        </div>

        {/* Vehicle & Service */}
        <div className="detail-section service-section">
          <h3>
            <FaTruck /> Vehicle & Service
          </h3>
          <div className="detail-content">
            <div className="detail-row">
              <label>Vehicle Type:</label>
              <span className="badge vehicle-badge">{quote.vehicleType}</span>
            </div>
            <div className="detail-row">
              <label>Service Type:</label>
              <span className="badge service-badge">{quote.serviceType}</span>
            </div>
            <div className="detail-row">
              <label>Real-Time Tracking:</label>
              <span className={`status-badge ${quote.realtimeTracking === 'yes' ? 'success' : 'default'}`}>
                {quote.realtimeTracking === 'yes' ? '✓ Required' : 'Not Required'}
              </span>
            </div>
            <div className="detail-row">
              <label>Insurance:</label>
              <span className={`status-badge ${quote.insuranceRequired === 'yes' ? 'success' : 'default'}`}>
                {quote.insuranceRequired === 'yes' ? '✓ Required' : 'Not Required'}
              </span>
            </div>
          </div>
        </div>

        {/* Special Instructions */}
        {quote.specialInstructions && (
          <div className="detail-section full-width">
            <h3>Special Instructions</h3>
            <div className="detail-content">
              <p className="special-instructions">{quote.specialInstructions}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default QuoteDetail;

